import React from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacing from '../../spacing';

// Button variants
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'link' | 'danger';

// Button sizes
export type ButtonSize = 'small' | 'medium' | 'large';

// Button props
export interface ButtonProps {
  /**
   * The variant of the button
   * @default 'primary'
   */
  variant?: ButtonVariant;
  
  /**
   * The size of the button
   * @default 'medium'
   */
  size?: ButtonSize;
  
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  
  /**
   * Whether the button should take the full width of its container
   * @default false
   */
  fullWidth?: boolean;
  
  /**
   * The content of the button
   */
  children: React.ReactNode;
  
  /**
   * Optional click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * The type of the button
   * @default 'button'
   */
  type?: 'button' | 'submit' | 'reset';
  
  /**
   * Optional start icon
   */
  startIcon?: React.ReactNode;
  
  /**
   * Optional end icon
   */
  endIcon?: React.ReactNode;
}

// Define StyledButtonProps with transient props
interface StyledButtonProps {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $fullWidth?: boolean;
  $hasStartIcon?: boolean;
  $hasEndIcon?: boolean;
}

// Variant styles
const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return css`
        background-color: ${colors.primary[500]};
        color: white;
        border: none;
        
        &:hover:not(:disabled) {
          background-color: ${colors.primary[600]};
        }
        
        &:active:not(:disabled) {
          background-color: ${colors.primary[700]};
        }
      `;
    case 'secondary':
      return css`
        background-color: ${colors.neutral[100]};
        color: ${colors.primary[500]};
        border: 1px solid ${colors.primary[500]};
        
        &:hover:not(:disabled) {
          background-color: ${colors.neutral[200]};
        }
        
        &:active:not(:disabled) {
          background-color: ${colors.neutral[300]};
        }
      `;
    case 'tertiary':
      return css`
        background-color: transparent;
        color: ${colors.primary[500]};
        border: none;
        
        &:hover:not(:disabled) {
          background-color: ${colors.neutral[100]};
        }
        
        &:active:not(:disabled) {
          background-color: ${colors.neutral[200]};
        }
      `;
    case 'ghost':
      return css`
        background-color: transparent;
        color: ${colors.neutral[700]};
        border: none;
        
        &:hover:not(:disabled) {
          background-color: ${colors.neutral[100]};
        }
        
        &:active:not(:disabled) {
          background-color: ${colors.neutral[200]};
        }
      `;
    case 'link':
      return css`
        background-color: transparent;
        color: ${colors.primary[500]};
        border: none;
        padding: 0;
        text-decoration: underline;

        &:hover:not(:disabled) {
          color: ${colors.primary[600]};
        }

        &:active:not(:disabled) {
          color: ${colors.primary[700]};
        }
      `;
    case 'danger':
      return css`
        background-color: var(--error-default, #EF4444);
        color: white;
        border: none;

        &:hover:not(:disabled) {
          background-color: var(--error-default, #DC2626);
        }

        &:active:not(:disabled) {
          background-color: #B91C1C;
        }
      `;
    default:
      return '';
  }
};

// Size styles
const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: 8px 16px;
        font-size: ${typography.fontSize.sm};
        height: 32px;
      `;
    case 'medium':
      return css`
        padding: ${spacing.spacing.sm} ${spacing.spacing.md};
        font-size: ${typography.fontSize.md};
        height: 40px;
      `;
    case 'large':
      return css`
        padding: ${spacing.spacing.md} ${spacing.spacing.lg};
        font-size: ${typography.fontSize.lg};
        height: 48px;
      `;
    default:
      return '';
  }
};

// Styled button component
const StyledButton = styled.button<StyledButtonProps>`
  /* Base styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  outline: none;
  
  /* Variant styles */
  ${props => getVariantStyles(props.$variant || 'primary')}
  
  /* Size styles */
  ${props => getSizeStyles(props.$size || 'medium')}
  
  /* Full width */
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  
  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Icon spacing */
  ${props => props.$hasStartIcon && css`
    & > *:first-child {
      margin-right: ${spacing.spacing.xs};
    }
  `}
  
  ${props => props.$hasEndIcon && css`
    & > *:last-child {
      margin-left: ${spacing.spacing.xs};
    }
  `}
`;

/**
 * Button component for user interactions
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      disabled = false,
      fullWidth = false,
      children,
      onClick,
      className,
      type = 'button',
      startIcon,
      endIcon,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $hasStartIcon={Boolean(startIcon)}
        $hasEndIcon={Boolean(endIcon)}
        disabled={disabled}
        onClick={onClick}
        className={className}
        type={type}
        {...rest}
      >
        {startIcon}
        {children}
        {endIcon}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;